import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { decksAPI } from '../services/api';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import {
  Plus, Trash2, Play, BookOpen, X, Loader,
  PenLine, ChevronDown, ChevronUp, Pencil, Check
} from 'lucide-react';

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'fi', label: 'Finnish' },
  { code: 'fa', label: 'Persian' },
];
const CEFR = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export default function Decks() {
  const navigate = useNavigate();
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Deck modals
  const [showDeckModal, setShowDeckModal] = useState(false);
  const [editDeckModal, setEditDeckModal] = useState(null); // deck object
  const [deckForm, setDeckForm] = useState({ title: '', description: '', is_public: false });
  const [editDeckForm, setEditDeckForm] = useState({ title: '', description: '', is_public: false });

  // Card modals
  const [showCardModal, setShowCardModal] = useState(null); // deck_id
  const [editCardModal, setEditCardModal] = useState(null); // {card, deck_id}
  const [cardForm, setCardForm] = useState({ word: '', translation: '', example_sentence: '', cefr_level: 'B1', source_lang: 'fi', target_lang: 'en' });
  const [editCardForm, setEditCardForm] = useState({ word: '', translation: '', example_sentence: '', cefr_level: 'B1' });

  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedDeck, setExpandedDeck] = useState(null);
  const [deckCards, setDeckCards] = useState({});

  const load = async () => {
    try {
      const r = await decksAPI.list();
      setDecks(r.data);
    } catch { toast.error('Failed to load decks'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const loadCards = async (deckId) => {
    try {
      const r = await decksAPI.getFlashcards(deckId);
      setDeckCards(prev => ({ ...prev, [deckId]: r.data }));
    } catch {}
  };

  const toggleExpand = (deckId) => {
    if (expandedDeck === deckId) { setExpandedDeck(null); return; }
    setExpandedDeck(deckId);
    loadCards(deckId);
  };

  // ── CREATE DECK ──
  const handleCreateDeck = async (e) => {
    e.preventDefault();
    if (!deckForm.title.trim()) { toast.error('Title is required'); return; }
    setCreating(true);
    try {
      await decksAPI.create(deckForm.title, deckForm.description, deckForm.is_public);
      toast.success('Deck created ✓');
      setShowDeckModal(false);
      setDeckForm({ title: '', description: '', is_public: false });
      await load();
    } catch { toast.error('Failed to create deck'); }
    finally { setCreating(false); }
  };

  // ── EDIT DECK ──
  const openEditDeck = (deck) => {
    setEditDeckForm({ title: deck.title, description: deck.description || '', is_public: deck.is_public });
    setEditDeckModal(deck);
  };

  const handleEditDeck = async (e) => {
    e.preventDefault();
    if (!editDeckForm.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const r = await decksAPI.update(editDeckModal.id, editDeckForm);
      setDecks(prev => prev.map(d => d.id === editDeckModal.id ? { ...d, ...r.data } : d));
      toast.success('Deck updated ✓');
      setEditDeckModal(null);
    } catch { toast.error('Failed to update deck'); }
    finally { setSaving(false); }
  };

  // ── DELETE DECK ──
  const handleDeleteDeck = async (id, title) => {
    if (!window.confirm(`Delete deck "${title}"?`)) return;
    setDeletingId(id);
    try {
      await decksAPI.delete(id);
      toast.success('Deck deleted');
      setDecks(d => d.filter(x => x.id !== id));
      if (expandedDeck === id) setExpandedDeck(null);
    } catch { toast.error('Failed to delete'); }
    finally { setDeletingId(null); }
  };

  // ── ADD CARD ──
  const handleAddCard = async (e) => {
    e.preventDefault();
    if (!cardForm.word.trim()) { toast.error('Word is required'); return; }
    setCreating(true);
    try {
      await decksAPI.addFlashcard(showCardModal, { ...cardForm, deck_id: showCardModal });
      toast.success(`"${cardForm.word}" added ✓`);
      setCardForm({ word: '', translation: '', example_sentence: '', cefr_level: 'B1', source_lang: 'fi', target_lang: 'en' });
      if (expandedDeck === showCardModal) loadCards(showCardModal);
      await load();
    } catch { toast.error('Failed to add card'); }
    finally { setCreating(false); }
  };

  // ── EDIT CARD ──
  const openEditCard = (card, deckId) => {
    setEditCardForm({
      word: card.word,
      translation: card.translation,
      example_sentence: card.example_sentence || '',
      cefr_level: card.cefr_level || 'B1',
    });
    setEditCardModal({ card, deckId });
  };

  const handleEditCard = async (e) => {
    e.preventDefault();
    if (!editCardForm.word.trim()) { toast.error('Word is required'); return; }
    setSaving(true);
    try {
      const r = await decksAPI.updateFlashcard(editCardModal.deckId, editCardModal.card.id, editCardForm);
      setDeckCards(prev => ({
        ...prev,
        [editCardModal.deckId]: prev[editCardModal.deckId]?.map(c =>
          c.id === editCardModal.card.id ? r.data : c
        ),
      }));
      toast.success('Card updated ✓');
      setEditCardModal(null);
    } catch { toast.error('Failed to update card'); }
    finally { setSaving(false); }
  };

  // ── DELETE CARD ──
  const handleDeleteCard = async (card, deckId) => {
    if (!window.confirm(`Delete card "${card.word}"?`)) return;
    try {
      await decksAPI.deleteFlashcard(deckId, card.id);
      setDeckCards(prev => ({
        ...prev,
        [deckId]: prev[deckId]?.filter(c => c.id !== card.id),
      }));
      toast.success('Card deleted');
      await load();
    } catch { toast.error('Failed to delete card'); }
  };

  const modalStyle = {
    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(4px)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
    },
    card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1.5rem', width: '100%', maxWidth: 460 },
  };

  return (
    <Layout>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1>My Decks</h1>
          <p>Organize and practice your flashcards</p>
        </div>
        <button className="btn btn-primary" style={{ width: 'auto' }} onClick={() => setShowDeckModal(true)}>
          <Plus size={15} /> New Deck
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
          <Loader size={22} className="spin" />
        </div>
      ) : decks.length === 0 ? (
        <div className="empty-state">
          <BookOpen size={38} />
          <h3>No decks yet</h3>
          <p>Create your first deck and start adding flashcards</p>
          <button className="btn btn-primary" style={{ width: 'auto', marginTop: '1rem' }} onClick={() => setShowDeckModal(true)}>
            <Plus size={15} /> Create First Deck
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
          {decks.map(deck => (
            <div key={deck.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {/* Deck header */}
              <div style={{ padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <h3 style={{ fontWeight: 700, fontSize: '0.95rem' }}>{deck.title}</h3>
                    {deck.is_public && (
                      <span style={{ fontSize: '0.63rem', background: 'rgba(61,214,140,0.1)', color: 'var(--green)', padding: '0.12rem 0.4rem', borderRadius: 4, fontWeight: 700, border: '1px solid rgba(61,214,140,0.2)' }}>
                        PUBLIC
                      </span>
                    )}
                  </div>
                  {deck.description && (
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>{deck.description}</p>
                  )}
                  <p style={{ fontSize: '0.72rem', color: 'var(--text-faint)', marginTop: '0.25rem' }}>
                    {deck.flashcard_count || 0} cards
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, alignItems: 'center' }}>
                  <button className="btn btn-success" style={{ width: 'auto', padding: '0.5rem 0.9rem', fontSize: '0.8rem' }}
                    onClick={() => navigate(`/practice/${deck.id}`)}>
                    <Play size={12} /> Practice
                  </button>
                  <button className="btn btn-icon" onClick={() => openEditDeck(deck)} title="Edit deck">
                    <Pencil size={13} />
                  </button>
                  <button className="btn btn-icon" onClick={() => setShowCardModal(deck.id)} title="Add card">
                    <PenLine size={13} />
                  </button>
                  <button className="btn btn-icon" onClick={() => toggleExpand(deck.id)} title="View cards">
                    {expandedDeck === deck.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  </button>
                  <button className="btn btn-danger" style={{ padding: '0.5rem 0.65rem' }}
                    onClick={() => handleDeleteDeck(deck.id, deck.title)}
                    disabled={deletingId === deck.id}>
                    {deletingId === deck.id ? <Loader size={12} className="spin" /> : <Trash2 size={12} />}
                  </button>
                </div>
              </div>

              {/* Expanded cards */}
              {expandedDeck === deck.id && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '1rem 1.25rem', background: 'var(--bg-deep)' }}>
                  {!deckCards[deck.id] ? (
                    <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1rem' }}>
                      <Loader size={16} className="spin" />
                    </div>
                  ) : deckCards[deck.id].length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', textAlign: 'center' }}>
                      No cards yet — add one manually or via Translate
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {deckCards[deck.id].map(card => (
                        <div key={card.id} style={{
                          background: 'var(--bg-card)', border: '1px solid var(--border)',
                          borderRadius: 9, padding: '0.7rem 1rem',
                          display: 'flex', alignItems: 'center', gap: '0.8rem',
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{card.word}</span>
                              {card.translation && (
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>— {card.translation}</span>
                              )}
                              {card.cefr_level && (
                                <span className={`badge badge-${card.cefr_level.toLowerCase()}`}>{card.cefr_level}</span>
                              )}
                            </div>
                            {card.example_sentence && (
                              <p style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginTop: '0.2rem', fontStyle: 'italic' }}>
                                {card.example_sentence}
                              </p>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                            <button className="btn btn-icon" style={{ width: 28, height: 28 }}
                              onClick={() => openEditCard(card, deck.id)} title="Edit card">
                              <Pencil size={11} />
                            </button>
                            <button className="btn btn-danger" style={{ width: 28, height: 28, padding: 0 }}
                              onClick={() => handleDeleteCard(card, deck.id)} title="Delete card">
                              <Trash2 size={11} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── NEW DECK MODAL ── */}
      {showDeckModal && (
        <div style={modalStyle.overlay} onClick={e => e.target === e.currentTarget && setShowDeckModal(false)}>
          <div style={modalStyle.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>New Deck</h2>
              <button className="btn btn-icon" onClick={() => setShowDeckModal(false)}><X size={15} /></button>
            </div>
            <form onSubmit={handleCreateDeck}>
              <div className="form-group">
                <label>Title *</label>
                <input value={deckForm.title} onChange={e => setDeckForm({ ...deckForm, title: e.target.value })}
                  placeholder="e.g. Daily Finnish Words" autoFocus />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <input value={deckForm.description} onChange={e => setDeckForm({ ...deckForm, description: e.target.value })}
                  placeholder="What's this deck about?" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.1rem' }}>
                <input type="checkbox" id="is_public_new" checked={deckForm.is_public}
                  onChange={e => setDeckForm({ ...deckForm, is_public: e.target.checked })}
                  style={{ accentColor: 'var(--accent)', width: 15, height: 15 }} />
                <label htmlFor="is_public_new" style={{ fontSize: '0.83rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  Make this deck public
                </label>
              </div>
              <button className="btn btn-primary" type="submit" disabled={creating}>
                {creating ? 'Creating…' : 'Create Deck'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── EDIT DECK MODAL ── */}
      {editDeckModal && (
        <div style={modalStyle.overlay} onClick={e => e.target === e.currentTarget && setEditDeckModal(null)}>
          <div style={modalStyle.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Edit Deck</h2>
              <button className="btn btn-icon" onClick={() => setEditDeckModal(null)}><X size={15} /></button>
            </div>
            <form onSubmit={handleEditDeck}>
              <div className="form-group">
                <label>Title *</label>
                <input value={editDeckForm.title} onChange={e => setEditDeckForm({ ...editDeckForm, title: e.target.value })}
                  placeholder="Deck title" autoFocus />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <input value={editDeckForm.description} onChange={e => setEditDeckForm({ ...editDeckForm, description: e.target.value })}
                  placeholder="What's this deck about?" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.1rem' }}>
                <input type="checkbox" id="is_public_edit" checked={editDeckForm.is_public}
                  onChange={e => setEditDeckForm({ ...editDeckForm, is_public: e.target.checked })}
                  style={{ accentColor: 'var(--accent)', width: 15, height: 15 }} />
                <label htmlFor="is_public_edit" style={{ fontSize: '0.83rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  Make this deck public
                </label>
              </div>
              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <button className="btn btn-secondary" type="button" style={{ flex: 1 }} onClick={() => setEditDeckModal(null)}>
                  Cancel
                </button>
                <button className="btn btn-primary" type="submit" style={{ flex: 1 }} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ADD CARD MODAL ── */}
      {showCardModal && (
        <div style={modalStyle.overlay} onClick={e => e.target === e.currentTarget && setShowCardModal(null)}>
          <div style={{ ...modalStyle.card, maxWidth: 500 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Add Card Manually</h2>
              <button className="btn btn-icon" onClick={() => setShowCardModal(null)}><X size={15} /></button>
            </div>
            <form onSubmit={handleAddCard}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.9rem' }}>
                <div className="form-group">
                  <label>Word / Phrase *</label>
                  <input value={cardForm.word} onChange={e => setCardForm({ ...cardForm, word: e.target.value })}
                    placeholder="e.g. kirja" autoFocus />
                </div>
                <div className="form-group">
                  <label>Translation</label>
                  <input value={cardForm.translation} onChange={e => setCardForm({ ...cardForm, translation: e.target.value })}
                    placeholder="e.g. book" />
                </div>
              </div>
              <div className="form-group">
                <label>Example Sentence</label>
                <input value={cardForm.example_sentence} onChange={e => setCardForm({ ...cardForm, example_sentence: e.target.value })}
                  placeholder="e.g. Minulla on kirja." />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 0.9rem' }}>
                <div className="form-group">
                  <label>CEFR Level</label>
                  <select value={cardForm.cefr_level} onChange={e => setCardForm({ ...cardForm, cefr_level: e.target.value })} style={{ width: '100%' }}>
                    {CEFR.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Word Lang</label>
                  <select value={cardForm.source_lang} onChange={e => setCardForm({ ...cardForm, source_lang: e.target.value })} style={{ width: '100%' }}>
                    {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Translate to</label>
                  <select value={cardForm.target_lang} onChange={e => setCardForm({ ...cardForm, target_lang: e.target.value })} style={{ width: '100%' }}>
                    {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </select>
                </div>
              </div>
              <button className="btn btn-primary" type="submit" disabled={creating}>
                {creating ? 'Adding…' : 'Add Card'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── EDIT CARD MODAL ── */}
      {editCardModal && (
        <div style={modalStyle.overlay} onClick={e => e.target === e.currentTarget && setEditCardModal(null)}>
          <div style={{ ...modalStyle.card, maxWidth: 500 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Edit Card</h2>
              <button className="btn btn-icon" onClick={() => setEditCardModal(null)}><X size={15} /></button>
            </div>
            <form onSubmit={handleEditCard}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.9rem' }}>
                <div className="form-group">
                  <label>Word / Phrase *</label>
                  <input value={editCardForm.word} onChange={e => setEditCardForm({ ...editCardForm, word: e.target.value })}
                    placeholder="Word" autoFocus />
                </div>
                <div className="form-group">
                  <label>Translation</label>
                  <input value={editCardForm.translation} onChange={e => setEditCardForm({ ...editCardForm, translation: e.target.value })}
                    placeholder="Translation" />
                </div>
              </div>
              <div className="form-group">
                <label>Example Sentence</label>
                <input value={editCardForm.example_sentence} onChange={e => setEditCardForm({ ...editCardForm, example_sentence: e.target.value })}
                  placeholder="Example sentence" />
              </div>
              <div className="form-group">
                <label>CEFR Level</label>
                <select value={editCardForm.cefr_level} onChange={e => setEditCardForm({ ...editCardForm, cefr_level: e.target.value })} style={{ width: '100%' }}>
                  {CEFR.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <button className="btn btn-secondary" type="button" style={{ flex: 1 }} onClick={() => setEditCardModal(null)}>
                  Cancel
                </button>
                <button className="btn btn-primary" type="submit" style={{ flex: 1 }} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}